import { getCoreUtils } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { AppFrameLoad } from "./AppFrameLoad";
import { AppMediaRules } from "./AppMediaRules";

/** Turns a contact card's number into "Open chat" once the frame has loaded (`AppBubbleKinds.LinkCards`). */
export class AppKindsFrame {
    private Frame: IKindsFrame | null = null;
    private Lookup: Record<string, string>;

    public constructor(Lookup: Record<string, string>) {
        this.Lookup = Lookup;
    }

    public Attach = (Frame: IKindsFrame): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Frame = Frame;
            Frame.addEventListener("load", this.OnLoad);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppKindsFrame.Attach");
        }
    };

    public OnLoad = (): void => {
        AppFrameLoad.Run(this.Frame, this.OnDoc, "AppKindsFrame.OnLoad");
    };

    private OnDoc = (Doc: IKindsDoc): void => {
        AppBubbleKinds.LinkCards(Doc, this.Lookup);
    };
}

// AppBubbleKinds - the markup a richer received message carries BESIDE its words (2026-10-14, "Richer received
// messages"): the "Forwarded" mark, a quote block for a reply, a link preview's title and an "Open map" link.
//
// ⭐ ONE SOURCE, HABITAT-FREE: the page writer (`Bridge\src\DossierHtml.ts`) imports it, and so can any app-side
// renderer, so the two halves draw the same thing. The words themselves (a location's place, a poll's options...) are
// in the message's `Body` and drawn as the body is.
// ⛔ EVERY VALUE IS ESCAPED BY THE CALLER'S ESCAPER (`kHTMLCore.EscapeAttr` on the page) - they came off somebody
// else's phone. A map address must begin `https://www.openstreetmap.org/` (built by the service, re-checked here).
// ⛔ NO INLINE STYLE: classes only; the rules (`Css`) ride on a page only when a bubble uses them, so a page with none
// of these kinds is byte for byte what it was (the TaskOff goldens).

/** What a bubble needs to draw its extras - a structural subset of `WAMessage`. */
export interface IAppKindsMsg {
    QuoteText?: string;
    QuoteFromMe?: boolean;
    Forwarded?: boolean;
    MapUrl?: string;
    LinkTitle?: string;
    PollOptions?: string[];
    PollVotes?: { Me: string[]; Them: string[] };
    DeletedHere?: boolean;
    CardNumbers?: string[];
    ReplyToMessageId?: string;
    /** (2026-10-22) A live photo / voice note / document not fetched yet: its `Media/` file name, so the live bubble
     *  gets the rebuilt page's "Download media" button (`DossierHtml.ContentOf`). Set by `LiveRules.ItemOf` only. */
    MediaName?: string;
    MediaDocName?: string;
    MediaSticker?: boolean;
}

export type AppKindsEscape = (Text: string) => string;

/** The DOM a live bubble is decorated through (a structural subset, so the suite drives it with a fake). */
export interface IKindsNode {
    textContent: string | null;
    className: string;
    id?: string;
    firstChild?: IKindsNode | null;
    getAttribute(Name: string): string | null;
    setAttribute(Name: string, Value: string): void;
    appendChild(Child: IKindsNode): unknown;
    insertBefore(Child: IKindsNode, Ref: IKindsNode | null): unknown;
    querySelector(Selector: string): IKindsNode | null;
    querySelectorAll(Selector: string): ArrayLike<IKindsNode>;
}

export interface IKindsDoc {
    createElement(Tag: string): IKindsNode;
    querySelector(Selector: string): IKindsNode | null;
    querySelectorAll(Selector: string): ArrayLike<IKindsNode>;
}

export interface IKindsFrame {
    contentDocument: IKindsDoc | null;
    addEventListener(Type: string, Listener: () => void): void;
}

export class AppBubbleKinds {
    public static readonly MapPrefix: string = "https://www.openstreetmap.org/?mlat=";

    /** Does this message draw anything here? */
    public static Has = (Msg: IAppKindsMsg): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            return Msg.Forwarded === true || AppBubbleKinds.Text(Msg.QuoteText) !== ""
                || AppBubbleKinds.Text(Msg.LinkTitle) !== "" || AppBubbleKinds.MapOk(Msg.MapUrl)
                || Msg.DeletedHere === true || Msg.PollVotes !== undefined || AppBubbleKinds.Cards(Msg).length > 0
                || AppBubbleKinds.MediaOk(Msg.MediaName);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppBubbleKinds.Has");
            return false;
        }
    };

    /** Above the words: "Forwarded", then the quote block (a link to the original when `QuoteHref` is not ""). */
    public static Head = (Msg: IAppKindsMsg, QuoteHref: string, Esc: AppKindsEscape): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            let Out: string = "";
            if (Msg.DeletedHere === true) { Out = Out + "<span class=\"wkdel\">Deleted on your phone</span>"; }
            if (Msg.Forwarded === true) { Out = Out + "<span class=\"wkfwd\">Forwarded</span>"; }
            const Quote: string = AppBubbleKinds.Text(Msg.QuoteText);
            if (Quote === "") { return Out; }
            let Who: string = "Them";
            if (Msg.QuoteFromMe === true) { Who = "You"; }
            const Inner: string = "<span class=\"wkqwho\">" + Esc(Who) + "</span><span class=\"wkqtxt\">" + Esc(Quote) + "</span>";
            if (new RegExp("^#m-[0-9]{12}(-[0-9]{1,5})?$").test(QuoteHref) === true) {
                return Out + "<a class=\"wkq\" href=\"" + Esc(QuoteHref) + "\">" + Inner + "</a>";
            }
            return Out + "<span class=\"wkq\">" + Inner + "</span>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppBubbleKinds.Head");
            return "";
        }
    };

    /** Below the words: the link preview's title and "Open map". */
    public static Tail = (Msg: IAppKindsMsg, Esc: AppKindsEscape): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            let Out: string = "";
            const Title: string = AppBubbleKinds.Text(Msg.LinkTitle);
            if (Title !== "") { Out = Out + "<span class=\"wklink\">" + Esc(Title) + "</span>"; }
            Out = Out + AppBubbleKinds.VotesHtml(Msg, Esc);
            for (const Num of AppBubbleKinds.Cards(Msg)) {
                Out = Out + "<span class=\"wkcard\" " + AppBubbleKinds.CardAttr + "=\"" + Esc(Num) + "\"></span>";
            }
            if (AppBubbleKinds.MapOk(Msg.MapUrl) === true) {
                Out = Out + "<a class=\"wkmap\" href=\"" + Esc(String(Msg.MapUrl)) + "\" target=\"_blank\" rel=\"noopener noreferrer\">Open map</a>";
            }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppBubbleKinds.Tail");
            return "";
        }
    };

    /** The attribute a contact card's number rides on; the app turns a number that is an archive chat into "Open chat". */
    public static readonly CardAttr: string = "data-wac-card";

    /**
     * A poll's tally (each option with who chose it) or an event's answers, from `PollVotes` (`ChangeBook` decrypted
     * them). "" when nobody's vote could be read. ⛔ Words only - nothing here is a control.
     */
    public static VotesHtml = (Msg: IAppKindsMsg, Esc: AppKindsEscape): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Rows: string[] = AppBubbleKinds.VoteLines(Msg);
            if (Rows.length === 0) { return ""; }
            let Out: string = "<ul class=\"wkpoll\">";
            for (const Row of Rows) { Out = Out + "<li>" + Esc(Row) + "</li>"; }
            return Out + "</ul>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppBubbleKinds.VotesHtml");
            return "";
        }
    };

    /** What the live feed hands over, by message id (`Remember`), until its bubble is drawn (`Decorate`). Bounded. */
    private static Held: Record<string, IAppKindsMsg> = {};
    private static HeldOrder: string[] = [];

    /**
     * The `kinds` member of a `/live` item (written by `LiveRules.ItemOf` from `Wire`) -> the fields, each checked by
     * its own `typeof`; anything else is dropped. Null = nothing usable.
     */
    public static Read = (Raw: unknown): IAppKindsMsg | null => {
        if (getCoreUtils().KErrorMode === true) { return null; }
        try {
            if (Raw === null || typeof Raw !== "object" || Array.isArray(Raw) === true) { return null; }
            const R: Record<string, unknown> = Raw as Record<string, unknown>;
            const Out: IAppKindsMsg = {};
            if (typeof R.QuoteText === "string") { Out.QuoteText = R.QuoteText.substring(0, 300); }
            if (R.QuoteFromMe === true) { Out.QuoteFromMe = true; }
            if (R.Forwarded === true) { Out.Forwarded = true; }
            if (typeof R.MapUrl === "string" && AppBubbleKinds.MapOk(R.MapUrl) === true) { Out.MapUrl = R.MapUrl; }
            if (typeof R.LinkTitle === "string") { Out.LinkTitle = R.LinkTitle.substring(0, 300); }
            if (typeof R.ReplyToMessageId === "string" && new RegExp("^[A-Za-z0-9_-]{1,64}$").test(R.ReplyToMessageId)) { Out.ReplyToMessageId = R.ReplyToMessageId; }
            Out.CardNumbers = AppBubbleKinds.Strings(R.CardNumbers, 12, 20);
            Out.PollOptions = AppBubbleKinds.Strings(R.PollOptions, 12, 200);
            if (R.PollVotes !== null && typeof R.PollVotes === "object") {
                const V: Record<string, unknown> = R.PollVotes as Record<string, unknown>;
                Out.PollVotes = { Me: AppBubbleKinds.Strings(V.Me, 12, 200), Them: AppBubbleKinds.Strings(V.Them, 12, 200) };
            }
            if (R.DeletedHere === true) { Out.DeletedHere = true; }
            if (typeof R.MediaName === "string" && AppBubbleKinds.MediaOk(R.MediaName) === true) {
                Out.MediaName = R.MediaName;
                if (typeof R.MediaDocName === "string") { Out.MediaDocName = R.MediaDocName.substring(0, 200); }
                if (R.MediaSticker === true) { Out.MediaSticker = true; }
            }
            if (AppBubbleKinds.Has(Out) === false) { return null; }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppBubbleKinds.Read");
            return null;
        }
    };

    /** The live wire's `kinds` for a message (the service side), or null when it draws nothing here. */
    public static Wire = (Msg: IAppKindsMsg): IAppKindsMsg | null => {
        if (getCoreUtils().KErrorMode === true) { return null; }
        try {
            if (AppBubbleKinds.Has(Msg) === false) { return null; }
            const Out: IAppKindsMsg = {};
            const Names: string[] = ["QuoteText", "QuoteFromMe", "Forwarded", "MapUrl", "LinkTitle", "PollOptions", "PollVotes",
                "DeletedHere", "CardNumbers", "ReplyToMessageId"];
            for (const Name of Names) {
                const V: unknown = (Msg as Record<string, unknown>)[Name];
                if (V !== undefined && V !== null && V !== "") { (Out as Record<string, unknown>)[Name] = V; }
            }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppBubbleKinds.Wire");
            return null;
        }
    };

    /** A `Media/<id>.<ext>` file name the page may ask for (`AppMediaRules.Allowed`) - never a path. */
    public static MediaOk = (Name: unknown): boolean => {
        if (typeof Name !== "string" || Name.length === 0 || Name.indexOf("/") >= 0) { return false; }
        return AppMediaRules.Allowed("Media/" + Name);
    };

    /**
     * ⛔ (2026-10-22) A LIVE PHOTO HAD NO WAY TO BE FETCHED: the live bubble showed "photo (not downloaded)" with no
     * button, and the open frame kept it until a reload, although the 15-min rebuild had long fetched and placed the
     * file. The rebuilt page's own button (`data-wac-download`), right after the `.nomedia` line; `AppMediaFill`'s
     * delegated click fetches it through `/media/download`.
     */
    private static DownloadNode = (Doc: IKindsDoc, Bubble: IKindsNode, K: IAppKindsMsg): void => {
        if (AppBubbleKinds.MediaOk(K.MediaName) === false) { return; }
        if (Bubble.querySelector("[data-wac-download]") !== null) { return; }
        const Line: IKindsNode | null = Bubble.querySelector(".nomedia");
        if (Line === null) { return; }
        const Btn: IKindsNode = Doc.createElement("button");
        Btn.setAttribute("type", "button");
        Btn.setAttribute("data-wac-download", String(K.MediaName));
        if (K.MediaSticker === true) { Btn.setAttribute("data-wac-sticker", "1"); }
        if (typeof K.MediaDocName === "string") { Btn.setAttribute("data-wac-docname", K.MediaDocName); }
        Btn.textContent = "Download media";
        Bubble.insertBefore(Btn, Bubble.querySelector(".nomedia ~ .txt, .t"));
    };

    /** Keep a live item's kinds until its bubble is drawn (at most 200 kept). */
    public static Remember = (Id: string, Raw: unknown): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Kinds: IAppKindsMsg | null = AppBubbleKinds.Read(Raw);
            if (Kinds === null || typeof Id !== "string" || Id.length === 0) { return; }
            if (AppBubbleKinds.Held[Id] === undefined) { AppBubbleKinds.HeldOrder.push(Id); }
            AppBubbleKinds.Held[Id] = Kinds;
            while (AppBubbleKinds.HeldOrder.length > 200) { delete AppBubbleKinds.Held[AppBubbleKinds.HeldOrder.shift() as string]; }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppBubbleKinds.Remember");
        }
    };

    /**
     * ⭐ THE ONE HOOK FROM `AppChatAppend.PlaceLive`: a live bubble gets what the rebuilt page would draw - "Forwarded",
     * the quote block (a link to the original when it is on the page), link title, poll tally, contact-card slots and
     * "Open map". Built node by node, every value as `textContent` / a checked attribute.
     */
    public static Decorate = (Doc: IKindsDoc, Bubble: IKindsNode): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Mid: string = String(Bubble.getAttribute("data-mid") || "");
            const K: IAppKindsMsg | undefined = AppBubbleKinds.Held[Mid];
            if (K === undefined) { return; }
            const First: IKindsNode | null = Bubble.querySelector(".nomedia, .txt");
            const Clock: IKindsNode | null = Bubble.querySelector(".t");
            if (K.DeletedHere === true) { Bubble.insertBefore(AppBubbleKinds.Span(Doc, "wkdel", "Deleted on your phone"), First); }
            if (K.Forwarded === true) { Bubble.insertBefore(AppBubbleKinds.Span(Doc, "wkfwd", "Forwarded"), First); }
            const Quote: string = AppBubbleKinds.Text(K.QuoteText);
            if (Quote !== "") { Bubble.insertBefore(AppBubbleKinds.QuoteNode(Doc, K, Quote), First); }
            const Title: string = AppBubbleKinds.Text(K.LinkTitle);
            if (Title !== "") { Bubble.insertBefore(AppBubbleKinds.Span(Doc, "wklink", Title), Clock); }
            AppBubbleKinds.VotesNode(Doc, Bubble, K, Clock);
            for (const Num of AppBubbleKinds.Cards(K)) {
                const Slot: IKindsNode = AppBubbleKinds.Span(Doc, "wkcard", "");
                Slot.setAttribute(AppBubbleKinds.CardAttr, Num);
                Bubble.insertBefore(Slot, Clock);
            }
            AppBubbleKinds.DownloadNode(Doc, Bubble, K);
            if (AppBubbleKinds.MapOk(K.MapUrl) === true) {
                const A: IKindsNode = Doc.createElement("a");
                A.className = "wkmap";
                A.setAttribute("href", String(K.MapUrl));
                A.setAttribute("target", "_blank");
                A.setAttribute("rel", "noopener noreferrer");
                A.textContent = "Open map";
                Bubble.insertBefore(A, Clock);
            }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppBubbleKinds.Decorate");
        }
    };

    /**
     * (2026-10-20) The quote block for a reply sent from THIS app, built from the answered message's own bubble on the
     * page (`[data-mid]`): its words (`.txt`, else the media line `.nomedia`), "You" on a `.me` bubble. Inserted above
     * the words, the same node `Decorate` makes. Nothing when the message is not on the page.
     */
    public static QuoteFrom = (Doc: IKindsDoc, Bubble: IKindsNode, Mid: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (new RegExp("^[A-Za-z0-9_-]{1,64}$").test(Mid) === false) { return; }
            const Orig: IKindsNode | null = Doc.querySelector("[data-mid=\"" + Mid + "\"]");
            if (Orig === null) { return; }
            let Words: IKindsNode | null = Orig.querySelector(".txt");
            if (Words === null) { Words = Orig.querySelector(".nomedia"); }
            const Quote: string = AppBubbleKinds.Text(String((Words !== null && Words.textContent) || "")).substring(0, 300);
            if (Quote === "") { return; }
            const K: IAppKindsMsg = { QuoteText: Quote, ReplyToMessageId: Mid };
            if ((" " + Orig.className + " ").indexOf(" me ") >= 0) { K.QuoteFromMe = true; }
            Bubble.insertBefore(AppBubbleKinds.QuoteNode(Doc, K, Quote), Bubble.querySelector(".nomedia, .txt"));
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppBubbleKinds.QuoteFrom");
        }
    };

    /** The quote block: an `<a href="#<id>">` when the original is on the page (by `data-mid`), else a span. */
    private static QuoteNode = (Doc: IKindsDoc, K: IAppKindsMsg, Quote: string): IKindsNode => {
        if (getCoreUtils().KErrorMode === true) { return Doc.createElement("span"); }
        try {
            let Href: string = "";
            const Reply: string = String(K.ReplyToMessageId || "");
            if (Reply.length > 0) {
                const Orig: IKindsNode | null = Doc.querySelector("[data-mid=\"" + Reply + "\"]");
                const Id: string = String((Orig !== null && Orig.getAttribute("id")) || "");
                if (new RegExp("^m-[0-9]{12}(-[0-9]{1,5})?$").test(Id) === true) { Href = "#" + Id; }
            }
            let Box: IKindsNode = Doc.createElement("span");
            if (Href !== "") { Box = Doc.createElement("a"); Box.setAttribute("href", Href); }
            Box.className = "wkq";
            let Who: string = "Them";
            if (K.QuoteFromMe === true) { Who = "You"; }
            Box.appendChild(AppBubbleKinds.Span(Doc, "wkqwho", Who));
            Box.appendChild(AppBubbleKinds.Span(Doc, "wkqtxt", Quote));
            return Box;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppBubbleKinds.QuoteNode");
            return Doc.createElement("span");
        }
    };

    /** The poll tally as `<ul class="wkpoll">`, from the same words `VotesHtml` writes on the page. */
    private static VotesNode = (Doc: IKindsDoc, Bubble: IKindsNode, K: IAppKindsMsg, Before: IKindsNode | null): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Lines: string[] = AppBubbleKinds.VoteLines(K);
            if (Lines.length === 0) { return; }
            const List: IKindsNode = Doc.createElement("ul");
            List.className = "wkpoll";
            for (const Line of Lines) {
                const Li: IKindsNode = Doc.createElement("li");
                Li.textContent = Line;
                List.appendChild(Li);
            }
            Bubble.insertBefore(List, Before);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppBubbleKinds.VotesNode");
        }
    };

    /** The tally's lines (shared by the page and the live bubble). */
    public static VoteLines = (Msg: IAppKindsMsg): string[] => {
        if (getCoreUtils().KErrorMode === true) { return []; }
        try {
            const V: { Me: string[]; Them: string[] } | undefined = Msg.PollVotes;
            if (V === undefined) { return []; }
            let Options: string[] = [];
            if (Array.isArray(Msg.PollOptions) === true) { Options = Msg.PollOptions as string[]; }
            const Rows: string[] = [];
            if (Options.length === 0) {
                if (V.Me.length > 0) { Rows.push("You: " + V.Me.join(", ")); }
                if (V.Them.length > 0) { Rows.push("Them: " + V.Them.join(", ")); }
            }
            for (const Opt of Options) {
                const Who: string[] = [];
                if (V.Me.indexOf(Opt) >= 0) { Who.push("You"); }
                if (V.Them.indexOf(Opt) >= 0) { Who.push("Them"); }
                let Line: string = Opt + " - " + String(Who.length) + " vote";
                if (Who.length !== 1) { Line = Line + "s"; }
                if (Who.length > 0) { Line = Line + " (" + Who.join(", ") + ")"; }
                Rows.push(Line);
            }
            return Rows;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppBubbleKinds.VoteLines");
            return [];
        }
    };

    /**
     * ⭐ "OPEN CHAT" ON A CONTACT CARD: every empty card slot whose number is a 1:1 chat in the archive (`Lookup`: E.164
     * -> the app's own `#c/...` address, absolute) becomes a link that opens it in the app (`target="_top"`). A number
     * that is not a chat stays as the card's words say it.
     */
    public static LinkCards = (Doc: IKindsDoc, Lookup: Record<string, string>): number => {
        if (getCoreUtils().KErrorMode === true) { return 0; }
        try {
            let Made: number = 0;
            const Slots: ArrayLike<IKindsNode> = Doc.querySelectorAll("[" + AppBubbleKinds.CardAttr + "]");
            for (let At: number = 0; At < Slots.length; At = At + 1) {
                const Slot: IKindsNode = Slots[At];
                const Go: string | undefined = Lookup[String(Slot.getAttribute(AppBubbleKinds.CardAttr) || "")];
                if (typeof Go !== "string" || String(Slot.textContent || "").length > 0) { continue; }
                const A: IKindsNode = Doc.createElement("a");
                A.className = "wkcardgo";
                A.setAttribute("href", Go);
                A.setAttribute("target", "_top");
                A.textContent = "Open chat";
                Slot.appendChild(A);
                Made = Made + 1;
            }
            return Made;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppBubbleKinds.LinkCards");
            return 0;
        }
    };

    /** The rules for these classes - only on a page that draws one (see the header). */
    public static Css = (): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            return ".b .wkfwd{display:block;font-size:12px;font-style:italic;opacity:.75;margin-bottom:2px}"
                + ".b .wkq{display:block;border-left:3px solid currentColor;padding:2px 8px;margin:0 0 4px;opacity:.85;"
                + "text-decoration:none;color:inherit;border-radius:4px;background:rgba(127,127,127,.12)}"
                + ".b .wkqwho{display:block;font-size:12px;font-weight:600}"
                + ".b .wkqtxt{display:block;font-size:13px;white-space:pre-wrap;overflow-wrap:anywhere}"
                + ".b .wklink{display:block;font-size:12px;font-weight:600;margin-top:4px}"
                + ".b .wkmap{display:inline-block;margin-top:4px;font-size:13px;font-weight:600}"
                + ".b .wkdoc{display:inline-block;font-weight:600;overflow-wrap:anywhere}"
                + ".b .sticker{display:block;width:128px;height:128px;object-fit:contain}"
                + ".b .wkpoll{display:block;margin-top:4px;font-size:13px}.b .wkpoll li{margin:2px 0}"
                + ".b .wkcard{display:inline-block;margin-top:4px;font-size:13px;font-weight:600}"
                + ".b .wkcard:empty{display:none}.b .wkcardgo{font-weight:600}"
                + ".b .wkdel{display:block;font-size:12px;font-style:italic;opacity:.75;margin-bottom:2px}";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppBubbleKinds.Css");
            return "";
        }
    };

    // DOCUMENTED EXCEPTION: pure readers; "" / false IS the absent field. ERROR-SUPPRESSED-JUSTIFIED: guarded callers.
    private static Text = (Value: unknown): string => {
        if (typeof Value !== "string") { return ""; }
        return Value.trim();
    };

    private static Span = (Doc: IKindsDoc, Cls: string, Text: string): IKindsNode => {
        const S: IKindsNode = Doc.createElement("span");
        S.className = Cls;
        S.textContent = Text;
        return S;
    };

    private static Strings = (V: unknown, Max: number, MaxLen: number): string[] => {
        if (Array.isArray(V) === false) { return []; }
        const Out: string[] = [];
        for (const One of V as unknown[]) {
            if (typeof One === "string" && Out.length < Max) { Out.push(One.substring(0, MaxLen)); }
        }
        return Out;
    };

    private static Cards = (Msg: IAppKindsMsg): string[] => {
        if (Array.isArray(Msg.CardNumbers) === false) { return []; }
        const Out: string[] = [];
        for (const N of Msg.CardNumbers as string[]) {
            if (typeof N === "string" && new RegExp("^\\+[0-9]{6,15}$").test(N) === true && Out.length < 12) { Out.push(N); }
        }
        return Out;
    };

    private static MapOk = (Value: unknown): boolean => {
        if (typeof Value !== "string" || Value.length > 200) { return false; }
        return Value.indexOf(AppBubbleKinds.MapPrefix) === 0 && new RegExp("^[A-Za-z0-9:/?.=&#_-]+$").test(Value);
    };
}
